/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { ReportingInProgress, ReportingDone } from 'secondstep-components'
import {
  LessonsTable,
  NotStartedIcon,
  SmallText,
  StatusBodyCopy,
  StatusTooltip,
} from './component.styles'
import {
  FlexContainer,
  TextContainer,
  TextHiddenOverflow,
} from 'components/ReportsTable/component.styles'
import {
  COL_SPAN_TWO,
  COL_SPAN_THREE,
  MOBILE_LARGE_VIEW_WIDTH,
} from './constants'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { toTitleCase } from 'utils/stringHelpers'
import { lessonTitleMap, unitTitleMap } from './unitAndLessonTitleMap'
dayjs.extend(localizedFormat)

const K8LessonsTable = ({ lessons, statusTooltip, variant = 'default' }) => {
  const [unitColspan, setUnitColspan] = useState(() =>
    window.innerWidth > MOBILE_LARGE_VIEW_WIDTH ? COL_SPAN_THREE : COL_SPAN_TWO,
  )

  const updateUnitColspan = () =>
    setUnitColspan(
      window.innerWidth > MOBILE_LARGE_VIEW_WIDTH
        ? COL_SPAN_THREE
        : COL_SPAN_TWO,
    )

  useEffect(() => {
    window.addEventListener('resize', updateUnitColspan)
    return () => window.removeEventListener('resize', updateUnitColspan)
  }, [])

  const addUnitRows = rows => {
    const formatUnitRowText = row => {
      const { parent, parentTitle } = row
      const unitNumber = `${toTitleCase(parent)}: `
      const unitTitle = unitTitleMap[parentTitle] || parentTitle
      if (variant === 'tier2') {
        return `Focus Area: ${unitTitle}`
      } else {
        return `${parentTitle.includes('rbh') ? '' : unitNumber}${unitTitle}`
      }
    }
    const rowsWithUnitRows = []
    if (rows.length > 0) {
      let currentUnit = rows[0].parent
      rowsWithUnitRows.push(formatUnitRowText(rows[0]))
      rows.forEach(r => {
        if (r.parent !== currentUnit) {
          currentUnit = r.parent
          rowsWithUnitRows.push(formatUnitRowText(r))
        }
        rowsWithUnitRows.push(r)
      })
    }
    return rowsWithUnitRows
  }
  const tableRows = addUnitRows(lessons)

  // TODO: componentize?
  const statusMap = {
    complete: { text: 'Done', Icon: ReportingDone },
    incomplete: {
      text: 'In Progress',
      Icon: ReportingInProgress,
      displayDate: true,
    },
    notStarted: { text: 'Not Started Yet' },
  }

  // Variant configurations for different contexts
  const getVariantConfig = () => {
    const configs = {
      default: {
        lessonsTitle: 'Lessons',
        markedDoneTitle: 'Marked Done On',
        markedDoneUseEllipsis: true,
        getStatusRenderer: () => row => {
          const { status, lastViewedDate } = row
          const { Icon, text, displayDate } = statusMap[status]
          return (
            <FlexContainer>
              {Icon && <Icon className="cell-icon" />}
              {!Icon && <NotStartedIcon className="cell-icon" />}
              <TextContainer>
                <div>{text}</div>
                {displayDate && (
                  <SmallText>
                    {dayjs(lastViewedDate).format('MM/DD/YYYY')}
                  </SmallText>
                )}
              </TextContainer>
            </FlexContainer>
          )
        },
      },
      tier2: {
        lessonsTitle: 'Tier 2 Lessons',
        markedDoneTitle: 'Date Marked Done',
        markedDoneUseEllipsis: false,
        getStatusRenderer: () => row => {
          return row.status === 'complete' ? 'Done' : '—'
        },
      },
    }
    return configs[variant] || configs.default
  }

  const variantConfig = getVariantConfig()

  const lessonsTitle = variantConfig.lessonsTitle
  const markedDoneTitle = variantConfig.markedDoneTitle
  const markedDoneUseEllipsis = variantConfig.markedDoneUseEllipsis
  const statusRenderer = variantConfig.getStatusRenderer()

  const columns = [
    {
      title: lessonsTitle,
      component: <span>{lessonsTitle}</span>,
      fieldValue: row => {
        if (typeof row === 'string') {
          return row
        }
        const { lessonNumber, lessonTitle } = row
        let userFriendlyLessonNumber = lessonNumber
        let userFriendlyLessonTitle = lessonTitleMap[lessonTitle] || lessonTitle

        // the numbers for bpu lessons should start at 1 and they do in the titles
        // but not the provided row.lessonNumber, so the condition below parses the
        // correct lesson number from the row.lessonTitle (i.e. 'ms g8 rbh l01')
        if (lessonTitle.includes('rbh')) {
          const lessonNumberFromTitle = lessonTitle.split('rbh l').pop()
          userFriendlyLessonNumber =
            parseInt(lessonNumberFromTitle, 10) || lessonNumber
        }

        return (
          <TextHiddenOverflow>
            {`${
              variant !== 'tier2' ? `${userFriendlyLessonNumber}. ` : ''
            }${userFriendlyLessonTitle}`}
          </TextHiddenOverflow>
        )
      },
    },
    {
      title: 'Status',
      component: (
        <span>
          Status
          <StatusTooltip
            content={<StatusBodyCopy>{statusTooltip}</StatusBodyCopy>}
          >
            <i className="fas fa-question-circle" />
          </StatusTooltip>
        </span>
      ),
      fieldValue: row => {
        if (typeof row === 'string') {
          return
        }
        return statusRenderer(row)
      },
    },
    {
      title: markedDoneTitle,
      component: (
        <FlexContainer>
          {markedDoneUseEllipsis ? (
            <TextHiddenOverflow>{markedDoneTitle}</TextHiddenOverflow>
          ) : (
            <span>{markedDoneTitle}</span>
          )}
        </FlexContainer>
      ),
      fieldValue: row => {
        if (typeof row === 'string') {
          return
        }
        return row.completedDate
          ? dayjs(row.completedDate).format('MM/DD/YYYY')
          : '—'
      },
    },
  ]
  return (
    <LessonsTable
      columns={columns}
      headThemeLight
      key="k8-lessons-table"
      multiColSpanValue={unitColspan}
      rows={tableRows}
    />
  )
}

K8LessonsTable.propTypes = {
  lessons: PropTypes.array,
  statusTooltip: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'tier2']),
}

export default K8LessonsTable
